// Enhanced UserContext with Offline Authentication Support
// Provides both online and offline authentication capabilities

import { Session, User } from "@supabase/supabase-js";
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { offlineConfig } from "../config/offlineConfig";
import { offlineFirstDataManager } from "../services/offlineFirstDataManager";
import { offlineFirstProfileService } from "../services/offlineFirstProfileService";
import { AuthState, offlineAuth } from "../utils/offlineAuth";
import { offlineSupabase } from "../utils/offlineSupabaseProxy";
import { supabase } from "../utils/supabaseClient";

// Enhanced interface with offline capabilities
export interface UserContextType {
  // Basic auth state
  user: User | null;
  session: Session | null;
  isLoading: boolean;

  // Offline capabilities
  isOnline: boolean;
  isOfflineMode: boolean;
  lastSync: number | null;
  fromCache: boolean;

  // Auth methods
  signIn: (email: string, password: string) => Promise<{ error: any; fromCache?: boolean }>;
  signUp: (email: string, password: string) => Promise<{ error: any; data: any }>;
  signOut: () => Promise<void>;
  updatePassword: (password: string) => Promise<{ error: any }>;

  // Offline methods
  canAccessOffline: () => Promise<boolean>;
  getOfflineCapabilities: () => Promise<string[]>;
  syncWithServer: () => Promise<void>;
}

// Create the context
const UserContext = createContext<UserContextType | undefined>(undefined);

// Provider component
export const UserProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);
  const [isOfflineMode, setIsOfflineMode] = useState<boolean>(false);
  const [lastSync, setLastSync] = useState<number | null>(null);
  const [fromCache, setFromCache] = useState<boolean>(false);
  const isInitialLoadRef = useRef<boolean>(true);
  const [hasInitialized, setHasInitialized] = useState<boolean>(false);

  const navigate = useNavigate();
  const location = useLocation();

  // Use refs to track previous values and prevent unnecessary updates
  const prevAuthStateRef = useRef<AuthState | null>(null);
  const isSubscribedRef = useRef(false);

  useEffect(() => {
    let mounted = true;

    // Setup initial authentication state
    const setupAuth = async () => {
      if (hasInitialized) {
        console.log('[UserContext] Already initialized, skipping setup');
        return;
      }

      try {
        console.log('[UserContext] Setting up authentication...');

        // First, try to get session directly from Supabase
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) {
          console.error('[UserContext] Error getting session:', sessionError);
        }

        let currentUser = null;
        let currentSession = null;
        let fromCache = false;

        if (session?.user) {
          console.log('[UserContext] Found active session:', session.user.email);
          currentUser = session.user;
          currentSession = session;
          fromCache = false;
        } else {
          // Fallback to offline auth service if no direct session
          console.log('[UserContext] No direct session, trying offline auth...');
          const authResult = await offlineAuth.getSession();

          console.log('[UserContext] Offline auth result:', {
            hasUser: !!authResult.user,
            hasSession: !!authResult.session,
            fromCache: authResult.fromCache,
            error: authResult.error?.message
          });

          currentUser = authResult.user;
          currentSession = authResult.session;
          fromCache = authResult.fromCache;
        }

        if (mounted) {
          setUser(currentUser);
          setSession(currentSession);
          setFromCache(fromCache);
        }

        // Initialize offline-first data manager for authenticated user (if enabled)
        if (currentUser && offlineConfig.shouldInitializeOfflineFeatures()) {
          try {
            if (offlineConfig.debugLogs) {
              console.log('[UserContext] Initializing offline-first data manager...');
            }
            await offlineFirstDataManager.initialize(currentUser);
            if (offlineConfig.debugLogs) {
              console.log('[UserContext] Offline-first data manager initialized');
            }

            // Ensure user profile exists (only if offline data is enabled)
            if (offlineConfig.offlineData) {
              const profileExists = await offlineFirstProfileService.profileExists(currentUser.id);
              if (!profileExists) {
                if (offlineConfig.debugLogs) {
                  console.log('[UserContext] Creating initial profile...');
                }
                await offlineFirstProfileService.createInitialProfile(currentUser);
              }
            }
          } catch (error) {
            console.error('[UserContext] Failed to initialize offline-first manager:', error);
          }
        } else if (offlineConfig.debugLogs && currentUser) {
          console.log('[UserContext] Offline features disabled by configuration');
        }

        if (fromCache) {
          console.log('[UserContext] Using cached authentication for user:', currentUser?.email);
        } else if (currentUser) {
          console.log('[UserContext] Using online authentication for user:', currentUser?.email);
        } else {
          console.log('[UserContext] No authentication found');
        }
      } catch (error) {
        console.error('[UserContext] Failed to setup authentication:', error);
      } finally {
        if (mounted) {
          setIsLoading(false);
          isInitialLoadRef.current = false;
          setHasInitialized(true);

          // After initialization, explicitly get current auth state since callback is deferred
          const currentState = offlineAuth.getCurrentState();
          if (currentState) {
            prevAuthStateRef.current = currentState;
            setIsOnline(currentState.isOnline);
            setIsOfflineMode(currentState.isOfflineMode);
            setLastSync(currentState.lastSync);
            if (currentState.user && currentState.session) {
              setUser(currentState.user);
              setSession(currentState.session);
            }
          }
        }
      }
    };

    setupAuth();

    // Subscribe to offline auth state changes - only once
    if (!isSubscribedRef.current) {
      isSubscribedRef.current = true;

      const unsubscribeOfflineAuth = offlineAuth.onAuthStateChange((authState: AuthState) => {
        if (!mounted || !hasInitialized) return;

        // Compare with previous state to prevent unnecessary updates
        const prevState = prevAuthStateRef.current;
        const hasChanged = !prevState ||
          prevState.user?.id !== authState.user?.id ||
          prevState.session?.access_token !== authState.session?.access_token ||
          prevState.isOnline !== authState.isOnline ||
          prevState.isOfflineMode !== authState.isOfflineMode ||
          prevState.lastSync !== authState.lastSync;

        if (!hasChanged) {
          return; // Skip update if nothing changed
        }

        prevAuthStateRef.current = authState;

        // Batch all state updates together to minimize re-renders
        const updates: Array<() => void> = [];

        if (prevState?.isOnline !== authState.isOnline) {
          updates.push(() => setIsOnline(authState.isOnline));
        }
        if (prevState?.isOfflineMode !== authState.isOfflineMode) {
          updates.push(() => setIsOfflineMode(authState.isOfflineMode));
        }
        if (prevState?.lastSync !== authState.lastSync) {
          updates.push(() => setLastSync(authState.lastSync));
        }
        if (prevState?.user?.id !== authState.user?.id ||
          prevState?.session?.access_token !== authState.session?.access_token) {
          updates.push(() => setUser(authState.user));
          updates.push(() => setSession(authState.session));
        }

        // Execute all updates in a batch using React's automatic batching
        // This ensures all state updates happen in a single render cycle
        React.startTransition(() => {
          updates.forEach(update => update());
        });

        // Set user ID in offline proxy for RPC calls
        if (authState.user && prevState?.user?.id !== authState.user.id) {
          offlineSupabase.setUserId(authState.user.id);
          console.log('[UserContext] Set user ID in offline proxy:', authState.user.id);
        }
      });

      // Always listen to Supabase auth changes (works offline too for cached sessions)
      let supabaseUnsubscribe: (() => void) | null = null;

      const { data: authListener } = supabase.auth.onAuthStateChange(
        async (event, session) => {
          if (!mounted) return;

          console.log('[UserContext] Supabase auth event:', event, !!session?.user);

          // Handle 403 Forbidden errors by signing out
          if (event === "SIGNED_OUT" || !session?.user) {
            console.log('[UserContext] User signed out or no session, clearing state');
            setUser(null);
            setSession(null);
            // Clear any cached data
            localStorage.removeItem('supabase.auth.token');
            sessionStorage.clear();
          } else if (session?.user) {
            if (event === "TOKEN_REFRESHED") {
              // Token refresh on tab focus — only update session, not user, and only if
              // the user ID is the same. Avoids full app re-render on every tab switch.
              setSession(prev => prev?.user?.id === session.user.id ? session : prev);
            } else {
              // Set user and session directly from the auth event
              console.log('[UserContext] Setting user and session from auth event');
              setUser(session.user);
              setSession(session);
              setFromCache(false); // This is a live session, not from cache
            }
          }

          // Handle navigation based on auth events
          if (
            event === "SIGNED_IN" &&
            session?.user &&
            !isInitialLoadRef.current
          ) {
            // Navigate to dashboard if user is on a public route (login, signup, etc.)
            // Use window.location.pathname (not the React Router `location` from the
            // outer scope) because the auth listener is set up once in a [] useEffect —
            // the React Router `location` value would be stale after the user navigates.
            const publicRoutes = ['/login', '/signup', '/forgot-password', '/update-password'];
            const isOnPublicRoute = publicRoutes.includes(window.location.pathname);

            if (isOnPublicRoute) {
              console.log("[UserContext] User signed in from public route, letting routing system handle navigation");
              // Let the routing system handle navigation to ensure onboarding check is performed
              navigate("/");
            } else {
              console.log("[UserContext] User signed in but staying on current page:", location.pathname);
            }
          } else if (event === "SIGNED_OUT") {
            console.log("[UserContext] Navigating to login after sign out");
            navigate("/login");
          } else if (event === "PASSWORD_RECOVERY") {
            navigate("/update-password");
          } else if (
            event === "TOKEN_REFRESHED" ||
            (event === "SIGNED_IN" && isInitialLoadRef.current)
          ) {
            console.log(
              "[UserContext] Session refreshed/initial load, staying on current page:",
              location.pathname
            );
          }

          isInitialLoadRef.current = false;
        }
      );

      supabaseUnsubscribe = () => authListener.subscription.unsubscribe();

      // Cleanup
      return () => {
        mounted = false;
        unsubscribeOfflineAuth();
        isSubscribedRef.current = false;
        if (supabaseUnsubscribe) {
          supabaseUnsubscribe();
        }
      };
    }

  }, []);

  // Sign in function - uses offline auth service
  const signIn = useCallback(async (email: string, password: string) => {
    try {
      setIsLoading(true);

      const result = await offlineAuth.signIn(email, password);

      if (result.error) {
        return { error: result.error, fromCache: result.fromCache };
      }

      // Success - state will be updated via auth state change listener
      return { error: null, fromCache: result.fromCache };
    } catch (error) {
      return { error: error };
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Sign up function - requires online connection
  const signUp = useCallback(async (email: string, password: string) => {
    if (!isOnline) {
      return {
        data: null,
        error: new Error('Sign up requires internet connection')
      };
    }

    try {
      const { data, error } = await supabase.auth.signUp({ email, password });
      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  }, [isOnline]);

  // Sign out function - uses offline auth service
  const signOut = useCallback(async () => {
    try {
      console.log('[UserContext] Starting sign out process...');
      setIsLoading(true);
      await offlineAuth.signOut();
      console.log('[UserContext] Sign out completed');

      // Direct navigation to login
      console.log('🚀 Navigating to login immediately');
      navigate('/login');

    } catch (error) {
      console.error('[UserContext] Sign out failed:', error);
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  // Update password function - requires online connection
  const updatePassword = useCallback(async (password: string) => {
    if (!isOnline) {
      return { error: new Error('Password update requires internet connection') };
    }

    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (!error) {
        navigate("/login");
      }
      return { error };
    } catch (error) {
      return { error };
    }
  }, [isOnline, navigate]);

  // Check if user can access app offline
  const canAccessOffline = useCallback(async (): Promise<boolean> => {
    if (!user) return false;
    return await offlineAuth.canAccessOffline(user.id);
  }, [user]);

  // Get offline capabilities for current user
  const getOfflineCapabilities = useCallback(async (): Promise<string[]> => {
    if (!user) return [];
    return await offlineAuth.getOfflineCapabilities(user.id);
  }, [user]);

  // Manual sync with server
  const syncWithServer = useCallback(async (): Promise<void> => {
    if (!isOnline) {
      throw new Error('Cannot sync while offline');
    }

    try {
      // Trigger a fresh session check which will update cache
      await offlineAuth.getSession();
    } catch (error) {
      console.error('[UserContext] Sync failed:', error);
      throw error;
    }
  }, [isOnline]);

  // Memoize context value to prevent unnecessary re-renders
  const value: UserContextType = useMemo(() => ({
    // Basic auth state
    user,
    session,
    isLoading,

    // Offline capabilities
    isOnline,
    isOfflineMode,
    lastSync,
    fromCache,

    // Auth methods
    signIn,
    signUp,
    signOut,
    updatePassword,

    // Offline methods
    canAccessOffline,
    getOfflineCapabilities,
    syncWithServer,
  }), [user, session, isLoading, isOnline, isOfflineMode, lastSync, fromCache,
    signIn, signUp, signOut, updatePassword, canAccessOffline,
    getOfflineCapabilities, syncWithServer]);

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

// Custom hook for using the user context
export const useUser = (): UserContextType => {
  const context = useContext(UserContext);

  if (context === undefined) {
    throw new Error("useUser must be used within a UserProvider");
  }

  return context;
};

// Backward compatibility exports
export const useOfflineUser = useUser;
export const OfflineUserProvider = UserProvider;
export type OfflineUserContextType = UserContextType;
